import React from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { lightTheme } from "../../styles/theme";
import type { NormalizedViewCount } from "../../utils/viewCount";

type Props = {
  viewCount: NormalizedViewCount;
  layout?: "sidebar" | "footer";
};

const NoticeCardViewStats: React.FC<Props> = ({
  viewCount,
  layout = "footer",
}) => {
  const { seen, total, percentage } = viewCount;
  const unread = Math.max(0, total - seen);
  const isSidebar = layout === "sidebar";

  if (isSidebar) {
    return (
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          py: 0.25,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 500,
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontSize: 10,
            mb: 0.5,
          }}
        >
          Views
        </Typography>
        <Typography
          component="span"
          variant="body1"
          sx={{
            display: "block",
            fontWeight: 400,
            color: "primary.main",
            lineHeight: 1.1,
            fontSize: "1.25rem",
          }}
        >
          {percentage}%
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontWeight: 500,
            mt: 0.25,
          }}
        >
          {seen} of {total}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            mt: 1,
            height: 5,
            borderRadius: 3,
            backgroundColor: lightTheme.colors.borderSlateLight,
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              backgroundColor: lightTheme.colors.primary,
            },
          }}
        />
        {total > 0 && unread > 0 && percentage < 100 && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              color: "text.secondary",
              fontSize: 10,
              lineHeight: 1.3,
            }}
          >
            {unread} awaiting
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1.25,
        pt: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        bgcolor: lightTheme.colors.surfaceTealMist,
        border: "1px solid",
        borderColor: lightTheme.colors.borderSlateLight,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 24,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, color: "text.secondary", flexShrink: 0 }}
        >
          Views
        </Typography>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            backgroundColor: lightTheme.colors.borderSlateLight,
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              backgroundColor: lightTheme.colors.primary,
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: "text.primary",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {seen}/{total} ({percentage}%)
        </Typography>
      </Box>
      {total > 0 && unread > 0 && percentage < 100 && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
        >
          {unread} awaiting view
        </Typography>
      )}
    </Box>
  );
};

export default NoticeCardViewStats;
